import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart reward tiers component.
 * Handles click-to-add for product reward variants when thresholds are met.
 * Auto-removes reward variants when cart drops below their threshold.
 * Progress bar and tier states are updated server-side via section re-rendering.
 */
class CartRewardTiers extends Component {
  static observedAttributes = ['data-remove-variants'];

  #removing = false;

  /**
   * Called when data-remove-variants attribute changes (after section morph).
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-remove-variants' && newValue && !this.#removing) {
      this.#removeVariants(newValue.split(','));
    }
  }

  /**
   * Removes reward variants from cart and dispatches a cart update event.
   * @param {string[]} keys - Line item keys to remove
   */
  async #removeVariants(keys) {
    if (!keys.length) return;
    this.#removing = true;

    try {
      const updates = {};
      for (const key of keys) {
        updates[key] = 0;
      }

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({ updates }),
        }),
      });

      if (!response.ok) {
        console.error('[cart-reward-tiers] Failed to remove reward', response.status);
        return;
      }

      const cart = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-reward-tiers', {
          source: 'cart-reward-tiers',
          itemCount: cart.item_count,
        }),
      );
    } catch (error) {
      console.error('[cart-reward-tiers]', error);
    } finally {
      this.#removing = false;
    }
  }

  /**
   * Adds a reward variant to cart. Called by the global click handler.
   * @param {Element} button - The clicked add button
   */
  async addReward(button) {
    const variantId = /** @type {HTMLElement} */ (button).dataset.variantId;

    if (!variantId || /** @type {HTMLButtonElement} */ (button).disabled) return;

    button.setAttribute('aria-busy', 'true');

    try {
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body }),
      });

      if (!response.ok) {
        console.error('[cart-reward-tiers] Failed to add reward', response.status);
        return;
      }

      const cart = await (await fetch('/cart.json')).json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-reward-tiers', {
          source: 'cart-reward-tiers',
          itemCount: cart.item_count,
        }),
      );
    } catch (error) {
      console.error('[cart-reward-tiers]', error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-reward-tiers')) {
  customElements.define('cart-reward-tiers', CartRewardTiers);
}

/**
 * Global click handler for reward tier add buttons.
 * Uses event delegation at document level so it works regardless of
 * DOM morphing, element replacement, or custom element lifecycle.
 */
document.addEventListener('click', (event) => {
  const button = /** @type {Element} */ (event.target).closest(
    'cart-reward-tiers [data-variant-id]',
  );
  if (!button) return;

  const component = /** @type {CartRewardTiers | null} */ (button.closest('cart-reward-tiers'));
  if (component?.addReward) {
    component.addReward(button);
  }
});
